import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import "./SellerApplicationPage.css";

const CATEGORIES = [
  "Sports Cards","Trading Card Games","Memorabilia & Autographs","Comics",
  "Coins & Currency","Toys & Collectibles","Other Collectibles"
];
// Keep a trailing hyphen while the user is typing; normalize only when validating/submitting.
const editSlug = value => String(value||"").toLowerCase().replace(/[^a-z0-9-]/g,"-").replace(/-{2,}/g,"-").replace(/^-+/,"");
const finalSlug = value => editSlug(value).replace(/-+$/,"");
const LEGAL_DOWNLOADS = {
  seller_agreement: "/legal/Hobbli_Seller_Agreement_2026.10.02-DRAFT-1.pdf",
  marketplace_rules: "/legal/Hobbli_Marketplace_Rules_2026.10.02-DRAFT-1.pdf"
};

function LegalReader({doc,read,setRead}) {
  const ref=useRef(null);
  const onScroll=()=>{
    const el=ref.current;
    if(el && el.scrollHeight-el.scrollTop-el.clientHeight<18) setRead(true);
  };
  return <section className="seller-legal-block">
    <div className="seller-legal-heading">
      <div><strong>{doc?.title}</strong><span>Version {doc?.version}</span></div>
      <a className="seller-legal-download" href={LEGAL_DOWNLOADS[doc?.document_key]} download target="_blank" rel="noreferrer">Download PDF</a>
    </div>
    <div ref={ref} onScroll={onScroll} className="seller-legal-scroll" tabIndex="0">
      <pre>{doc?.content||"Loading…"}</pre>
      <div className="seller-legal-end">End of {doc?.title}</div>
    </div>
    <div className={`seller-read-state ${read?"done":""}`}>{read?"✓ Read to the end":"Scroll to the bottom to continue"}</div>
  </section>;
}

export default function SellerApplicationPage({sellerAccess}) {
  const navigate=useNavigate();
  const [state,setState]=useState(null),[docs,setDocs]=useState([]);
  const [loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[notice,setNotice]=useState("");
  const [read,setRead]=useState({seller_agreement:false,marketplace_rules:false});
  const [acks,setAcks]=useState({agreement:false,rules:false,arbitration:false});
  const [form,setForm]=useState({shopName:"",shopSlug:"",description:"",categories:[]});
  const [availability,setAvailability]=useState({checking:false,name:null,slug:null,nameMessage:"",slugMessage:""});

  const load=useCallback(async()=>{
    setLoading(true); setNotice("");
    const [appRes,docRes]=await Promise.all([
      supabase.rpc("get_my_seller_application"),
      supabase.rpc("get_current_seller_legal_documents")
    ]);
    if(appRes.error){setNotice(appRes.error.message||"Unable to load seller application.");setLoading(false);return;}
    if(docRes.error){setNotice(docRes.error.message||"Unable to load seller terms.");setLoading(false);return;}
    const row=Array.isArray(appRes.data)?appRes.data[0]:appRes.data;
    setState(row||null); setDocs(docRes.data||[]);
    if(row?.shop_name)setForm({shopName:row.shop_name,shopSlug:row.shop_slug||"",description:row.shop_description||"",categories:row.item_categories||[]});
    setLoading(false);
  },[]);
  useEffect(()=>{load()},[load]);

  const isAdmin=sellerAccess?.isAdmin??sellerAccess?.is_admin??false;
  const approved=state?.application_status==="approved",pending=state?.application_status==="pending",rejected=state?.application_status==="rejected";
  const slugPreview=useMemo(()=>finalSlug(form.shopSlug||form.shopName),[form.shopSlug,form.shopName]);
  const agreement=docs.find(d=>d.document_key==="seller_agreement");
  const rules=docs.find(d=>d.document_key==="marketplace_rules");
  const legalReady=read.seller_agreement&&read.marketplace_rules&&acks.agreement&&acks.rules&&acks.arbitration;

  useEffect(()=>{
    const name=form.shopName.trim();
    const slug=finalSlug(form.shopSlug||form.shopName);
    if(!name && !slug){setAvailability({checking:false,name:null,slug:null,nameMessage:"",slugMessage:""});return;}
    let cancelled=false;
    const timer=setTimeout(async()=>{
      setAvailability(a=>({...a,checking:true}));
      const {data,error}=await supabase.rpc("check_marketplace_shop_identity_availability",{
        p_shop_name:name||null,p_shop_slug:slug||null
      });
      if(cancelled)return;
      if(error){setAvailability({checking:false,name:null,slug:null,nameMessage:"Availability check unavailable",slugMessage:"Availability check unavailable"});return;}
      const row=Array.isArray(data)?data[0]:data;
      setAvailability({
        checking:false,
        name:row?.shop_name_available??null,
        slug:row?.shop_slug_available??null,
        nameMessage:row?.shop_name_message||"",
        slugMessage:row?.shop_slug_message||""
      });
    },450);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[form.shopName,form.shopSlug]);

  const toggleCategory=c=>setForm(f=>({...f,categories:f.categories.includes(c)?f.categories.filter(x=>x!==c):[...f.categories,c]}));

  const submit=async e=>{
    e.preventDefault();
    if(!legalReady){setNotice("Please read both documents to the end and complete all acknowledgments.");return;}
    if(!form.categories.length){setNotice("Please select at least one type of item you plan to sell.");return;}
    if(availability.name===false||availability.slug===false){setNotice("Please choose an available shop name and URL.");return;}
    setBusy(true);setNotice("");
    const {error}=await supabase.rpc("submit_my_seller_application",{
      p_shop_name:form.shopName.trim(),p_shop_slug:slugPreview,p_shop_description:form.description.trim()||null,
      p_item_categories:form.categories,
      p_seller_agreement_version:agreement.version,p_seller_agreement_sha256:agreement.content_sha256,
      p_marketplace_rules_version:rules.version,p_marketplace_rules_sha256:rules.content_sha256,
      p_arbitration_acknowledged:true
    });
    if(error)setNotice(error.message||"Unable to submit seller application.");
    else{setNotice("Application submitted for review.");await load();}
    setBusy(false);
  };

  if(loading)return <main className="seller-apply-page"><div className="seller-apply-card">Loading seller application…</div></main>;
  if(isAdmin&&!state?.application_status)return <main className="seller-apply-page"><div className="seller-apply-card"><span className="seller-apply-kicker">Hobbli Marketplace</span><h1>Seller access</h1><p>Your administrator account can already access seller tools for marketplace testing.</p><button onClick={()=>navigate("/seller")}>Open Seller Dashboard</button></div></main>;
  if(approved)return <main className="seller-apply-page"><div className="seller-apply-card"><span className="seller-apply-status approved">Approved</span><h1>Your Hobbli shop is approved.</h1><p><strong>{state.shop_name}</strong> is ready for seller setup. Payment readiness and public checkout remain separate controls.</p><button onClick={()=>navigate("/seller")}>Open Seller Dashboard</button></div></main>;
  if(pending)return <main className="seller-apply-page"><div className="seller-apply-card"><span className="seller-apply-status pending">Pending review</span><h1>We have your seller application.</h1><p>Hobbli will review <strong>{state.shop_name}</strong>. Seller Dashboard access becomes available after approval.</p><div className="seller-apply-summary"><span>Requested shop</span><strong>{state.shop_name}</strong><small>/{state.shop_slug}</small><span>{(state.item_categories||[]).join(" · ")}</span></div></div></main>;

  return <main className="seller-apply-page"><div className="seller-apply-card seller-apply-card--wide">
    <span className="seller-apply-kicker">Sell on Hobbli</span>
    <h1>{rejected?"Update your seller application":"Apply for your Hobbli shop"}</h1>
    {rejected ? (
      <div className="seller-review-notice" role="status">
        <div className="seller-review-notice__title">Application needs changes</div>
        <div className="seller-review-notice__label">Hobbli review note</div>
        <div className="seller-review-notice__message">{state?.review_note||"Your previous application was not approved. Please review your application, make any needed changes, and submit it again."}</div>
        <div className="seller-review-notice__help">Update the application below, review the current Seller Agreement and Marketplace Rules again, then resubmit when you're ready.</div>
      </div>
    ) : (
      <p>Tell us about the shop you want to open. Shop names and URLs are unique and become your marketplace identity.</p>
    )}
    {notice&&<div className="seller-apply-notice">{notice}</div>}
    <form onSubmit={submit} className="seller-apply-form">
      <label>Shop name<input required maxLength="80" value={form.shopName} onChange={e=>setForm({...form,shopName:e.target.value})} placeholder="Steve's Cards"/>
        <small className={`identity-availability ${availability.name===true?"available":availability.name===false?"unavailable":""}`}>{availability.checking?"Checking availability…":availability.nameMessage||"Shop names must be unique. Changing an approved shop identity will require a controlled settings process."}</small>
      </label>
      <label>Shop URL<input required maxLength="70" value={form.shopSlug} onChange={e=>setForm({...form,shopSlug:editSlug(e.target.value)})} placeholder={finalSlug(form.shopName)||"my-hobbli-shop"}/>
        <small>hobbli.com/{slugPreview||"your-shop"}</small>
        <small className={`identity-availability ${availability.slug===true?"available":availability.slug===false?"unavailable":""}`}>{availability.checking?"Checking availability…":availability.slugMessage||"Lowercase letters, numbers, and hyphens are allowed."}</small>
      </label>

      <fieldset className="seller-category-field wide"><legend>What do you plan to sell?</legend><p>Select all that apply.</p><div className="seller-category-grid">{CATEGORIES.map(c=><label key={c} className={form.categories.includes(c)?"selected":""}><input type="checkbox" checked={form.categories.includes(c)} onChange={()=>toggleCategory(c)}/><span>{c}</span></label>)}</div></fieldset>

      <label className="wide">About your shop<textarea required maxLength="750" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Tell us what you collect, the kinds of items you expect to sell, and anything else that helps us understand your shop."/></label>

      <div className="seller-legal-intro wide"><h2>Seller Agreement & Marketplace Rules</h2><p>Read each document to the end. The acknowledgments remain locked until you have scrolled through both documents.</p></div>
      <div className="wide seller-legal-grid">
        <LegalReader doc={agreement} read={read.seller_agreement} setRead={()=>setRead(r=>({...r,seller_agreement:true}))}/>
        <LegalReader doc={rules} read={read.marketplace_rules} setRead={()=>setRead(r=>({...r,marketplace_rules:true}))}/>
      </div>

      <div className="seller-acknowledgments wide">
        <label className={!read.seller_agreement?"locked":""}><input type="checkbox" disabled={!read.seller_agreement} checked={acks.agreement} onChange={e=>setAcks({...acks,agreement:e.target.checked})}/><span>I have read and agree to the Hobbli Seller Agreement.</span></label>
        <label className={!read.marketplace_rules?"locked":""}><input type="checkbox" disabled={!read.marketplace_rules} checked={acks.rules} onChange={e=>setAcks({...acks,rules:e.target.checked})}/><span>I have read and agree to the Hobbli Marketplace Rules & Seller Standards.</span></label>
        <label className={!(read.seller_agreement&&read.marketplace_rules)?"locked arbitration": "arbitration"}><input type="checkbox" disabled={!(read.seller_agreement&&read.marketplace_rules)} checked={acks.arbitration} onChange={e=>setAcks({...acks,arbitration:e.target.checked})}/><span><strong>Dispute-resolution acknowledgment:</strong> I understand that the Seller Agreement contains an individual arbitration provision, jury-trial waiver, and class/collective-action waiver, subject to applicable law.</span></label>
      </div>

      <p className="seller-change-note wide">Hobbli may revise seller terms, marketplace rules, fees, standards, and policies as described in the Seller Agreement. Material revisions may require you to review and accept a new version before continuing to sell.</p>
      <div className="seller-apply-actions wide"><button type="button" className="secondary" onClick={()=>navigate("/shop")}>Back to Marketplace</button><button type="submit" disabled={busy||availability.checking||availability.name!==true||availability.slug!==true||!form.shopName.trim()||!slugPreview||!form.description.trim()||!form.categories.length||!legalReady}>{busy?"Submitting…":rejected?"Resubmit application":"Accept & Apply to Sell"}</button></div>
    </form>
  </div></main>;
}